import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ThumbsUp, Pin, Trash2, Edit, Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Forum = () => {
  const { token, user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [view, setView] = useState('categories'); // categories, posts, post-detail
  
  const [newPostDialog, setNewPostDialog] = useState(false);
  const [newPostData, setNewPostData] = useState({ title: '', content: '' });
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/forum/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load forum categories');
    }
  };

  const fetchPosts = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/forum/posts?category_id=${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
      setView('posts');
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load posts');
    }
  };

  const fetchPostDetail = async (postId) => {
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data);
      setView('post-detail');
    } catch (error) {
      console.error('Failed to fetch post:', error);
      toast.error('Failed to load post');
    }
  };

  const createPost = async () => {
    if (!newPostData.title || !newPostData.content) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/forum/posts`, {
        category_id: selectedCategory.id,
        title: newPostData.title,
        content: newPostData.content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Post created successfully!');
      setNewPostDialog(false);
      setNewPostData({ title: '', content: '' });
      fetchPosts(selectedCategory.id);
    } catch (error) {
      console.error('Failed to create post:', error);
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const createComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/forum/comments`, {
        post_id: selectedPost.id,
        content: newComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Comment added!');
      setNewComment('');
      fetchPostDetail(selectedPost.id);
    } catch (error) {
      console.error('Failed to create comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const likePost = async (postId) => {
    try {
      await axios.post(`${API}/forum/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (view === 'posts') {
        fetchPosts(selectedCategory.id);
      } else {
        fetchPostDetail(postId);
      }
    } catch (error) {
      console.error('Failed to like post:', error);
      toast.error('Failed to like post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Post deleted successfully');
      setView('posts');
      setSelectedPost(null);
      fetchPosts(selectedCategory.id);
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await axios.delete(`${API}/forum/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Comment deleted');
      fetchPostDetail(selectedPost.id);
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="forum-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Discussion Forums
          </h1>
          <p className="text-gray-600">Connect, share, and learn from the community</p>
        </div>

        {/* Categories View */}
        {view === 'categories' && (
          <div className="grid md:grid-cols-2 gap-4">
            {categories.map((category) => (
              <Card 
                key={category.id} 
                className="glass hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  setSelectedCategory(category);
                  fetchPosts(category.id);
                }}
                data-testid={`forum-category-${category.id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">{category.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{category.name}</h3>
                      <p className="text-gray-600 text-sm mb-3">{category.description}</p>
                      <Badge variant="outline" className="bg-rose-50">
                        {category.post_count || 0} posts
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Posts View */}
        {view === 'posts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setView('categories');
                    setSelectedCategory(null);
                    setPosts([]);
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Categories
                </Button>
                <div>
                  <h2 className="text-2xl font-bold">{selectedCategory?.icon} {selectedCategory?.name}</h2>
                  <p className="text-sm text-gray-600">{selectedCategory?.description}</p>
                </div>
              </div>
              <Button 
                className="bg-rose-600 hover:bg-rose-700"
                onClick={() => setNewPostDialog(true)}
                data-testid="new-forum-post-button"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>

            {posts.length === 0 ? (
              <Card className="glass">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No posts yet in this category</p>
                  <Button 
                    className="bg-rose-600 hover:bg-rose-700"
                    onClick={() => setNewPostDialog(true)}
                  >
                    Create the first post
                  </Button>
                </CardContent>
              </Card>
            ) : (
              posts.map((post) => (
                <Card 
                  key={post.id} 
                  className="glass hover:shadow-md transition-all cursor-pointer"
                  onClick={() => fetchPostDetail(post.id)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {post.is_pinned && <Pin className="w-4 h-4 text-rose-600" />}
                          <h3 className="text-lg font-semibold">{post.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{post.content}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="font-medium">{post.author_name}</span>
                          <span>•</span>
                          <span>{formatDate(post.created_at)}</span>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4" />
                            <span>{post.like_count}</span>
                          </div>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            <span>{post.comment_count}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Post Detail View */}
        {view === 'post-detail' && selectedPost && (
          <div className="space-y-6">
            <Button 
              variant="outline" 
              onClick={() => {
                setView('posts');
                setSelectedPost(null);
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Posts
            </Button>

            {/* Post Content */}
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {selectedPost.is_pinned && <Pin className="w-4 h-4 text-rose-600" />}
                      <h2 className="text-2xl font-bold">{selectedPost.title}</h2>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
                      <span className="font-medium">{selectedPost.author_name}</span>
                      <span>•</span>
                      <span>{formatDate(selectedPost.created_at)}</span>
                    </div>
                  </div>
                  {(selectedPost.author_id === user?.id || user?.role === 'admin') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deletePost(selectedPost.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <p className="text-gray-700 whitespace-pre-wrap mb-4">{selectedPost.content}</p>
                
                <div className="flex items-center gap-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => likePost(selectedPost.id)}
                    className={selectedPost.liked_by_user ? 'bg-rose-50 text-rose-600' : ''}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    {selectedPost.liked_by_user ? 'Liked' : 'Like'} ({selectedPost.like_count})
                  </Button>
                  <span className="text-sm text-gray-600">
                    {selectedPost.comment_count} {selectedPost.comment_count === 1 ? 'comment' : 'comments'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Comments */}
            <Card className="glass">
              <CardHeader>
                <CardTitle>Comments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* New Comment Form */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !loading && createComment()}
                  />
                  <Button
                    onClick={createComment}
                    disabled={loading || !newComment.trim()}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>

                {/* Comment List */}
                <div className="space-y-4 pt-4">
                  {selectedPost.comments && selectedPost.comments.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No comments yet. Be the first to comment!</p>
                  ) : (
                    selectedPost.comments?.map((comment) => (
                      <div key={comment.id} className="border-l-2 border-rose-200 pl-4 py-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{comment.author_name}</span>
                              <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                            </div>
                            <p className="text-gray-700 text-sm">{comment.content}</p>
                          </div>
                          {(comment.author_id === user?.id || user?.role === 'admin') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteComment(comment.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* New Post Dialog */}
        <Dialog open={newPostDialog} onOpenChange={setNewPostDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Post</DialogTitle>
              <DialogDescription>
                Share your thoughts with the community in {selectedCategory?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input
                  placeholder="Enter post title..."
                  value={newPostData.title}
                  onChange={(e) => setNewPostData({ ...newPostData, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content *</label>
                <Textarea
                  placeholder="Write your post content..."
                  value={newPostData.content}
                  onChange={(e) => setNewPostData({ ...newPostData, content: e.target.value })}
                  rows={8}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setNewPostDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={createPost}
                  disabled={loading}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {loading ? 'Creating...' : 'Create Post'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default Forum;
